import React, { useState, useEffect } from "react";
import Grid from "../Grid/Grid";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Fade,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import dashboardAPI from "../../Network/Dashboard";
import Title from "./Title";
import { selectRoles } from "../../reduxStore/RolesReducer";
import moment from "moment";
import CustomDateTimePicker from "../../Components/CustomDateTimePicker";
import helpers from '../../assets/js/helper';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const cookies = new Cookies();

const todayStart = new Date();
const yesterday = new Date(todayStart);
yesterday.setDate(yesterday.getDate() - 1);
yesterday.setHours(0, 0, 0, 0);

const todayEnd = new Date();
todayEnd.setHours(23, 59, 59, 999);

export default function ManageMizanWeight() {
  const [t] = useTranslation("common");
  const [pageSize, setPageSize] = useState(40);
  const [loading, setLoading] = useState(false);
  const [openFilterColumn, setOpenFilterColumn] = useState(false);
  const [openFilterColumnValue, setOpenFilterColumnValue] = useState([]);
  const [elementClicked, setElementClicked] = useState(null);

  // States for Category 1: Transformative (تحويلية)
  const [rowsCat1, setRowsCat1] = useState([]);
  const [pageNumberCat1, setPageNumberCat1] = useState(1);
  const [pageCountCat1, setPageCountCat1] = useState(0);
  const [rowsTotalCat1, setRowsTotalCat1] = useState(0);

  // States for Category 2: Press (مكبس)
  const [rowsCat2, setRowsCat2] = useState([]);
  const [pageNumberCat2, setPageNumberCat2] = useState(1);
  const [pageCountCat2, setPageCountCat2] = useState(0);
  const [rowsTotalCat2, setRowsTotalCat2] = useState(0);

  // Consolidated Header Object from Cookies
  const headerObject = {
    username: cookies.get("user_name") ? cookies.get("user_name") : null,
    user_id: cookies.get("user_id") ? cookies.get("user_id") : null,
    account_name: cookies.get("account_name") ? cookies.get("account_name") : null,
    account_id: cookies.get("account_id") ? cookies.get("account_id") : null,
    authHash: cookies.get("authHash") ? cookies.get("authHash") : null,
    sid: cookies.get("sid") ? cookies.get("sid") : null,
    baseurl: cookies.get("baseUrl") ? cookies.get("baseUrl") : null,
    id: cookies.get("id") ? cookies.get("id") : null,
    token: cookies.get("token") ? cookies.get("token") : null,
    language: cookies.get("language") || "ar",
  };

  // Adjustment dialog state
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [selectedMizan, setSelectedMizan] = useState(null);
  const [adjustmentValue, setAdjustmentValue] = useState(0);
  const [adjustmentNote, setAdjustmentNote] = useState("");
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);

  // New states for date-driven adjustment
  const [selectedAdjustmentDate, setSelectedAdjustmentDate] = useState(null);
  const [diffCalculationLoading, setDiffCalculationLoading] = useState(false);
  const [calculatedDiffData, setCalculatedDiffData] = useState(null);

  const [filters, setFilters] = useState({
    creation_date_to: moment().endOf('day').format('YYYY-MM-DD') + " 23:59:59",
    creationDateTo: todayEnd,
    all_time: 0
  });
  const [reload, setReload] = useState(false);

  const navigate = useNavigate();
  const controller = new AbortController();
  const screenwidth = useSelector((state) => state.settingsData.screenwidth);
  const rolesRedux = useSelector(selectRoles);

  // Open adjustment dialog
  const handleOpenAdjustment = (row) => {
    setSelectedMizan(row);
    setAdjustmentValue(0);
    setAdjustmentNote("");
    setSelectedAdjustmentDate(null);
    setCalculatedDiffData(null);
    setAdjustmentDialogOpen(true);
  };

  // Close adjustment dialog
  const handleCloseAdjustment = () => {
    setAdjustmentDialogOpen(false);
    setSelectedMizan(null);
    setAdjustmentValue(0);
    setAdjustmentNote("");
    setSelectedAdjustmentDate(null);
    setCalculatedDiffData(null);
  };

  // Date change handler (now just updates state)
  const handleDateChange = (newValue) => {
    if (!newValue) {
      setSelectedAdjustmentDate(null);
      setCalculatedDiffData(null);
      return;
    }
    // Handle dayjs vs Date
    const date = newValue.toDate ? newValue.toDate() : newValue;
    setSelectedAdjustmentDate(date);
    setCalculatedDiffData(null); // Clear previous results when date changes
  };

  // Filter date change handler
  const handleFilterDateChange = (newValue) => {
    if (!newValue) {
      setFilters({
        ...filters,
        creation_date_to: null,
        creationDateTo: null
      });
      return;
    }
    const date = newValue.toDate ? newValue.toDate() : newValue;
    setFilters({
      ...filters,
      creationDateTo: date,
      creation_date_to: helpers.formatDateTime(date)
    });
  };

  // Explicit calculation trigger
  const calculateWeightDifference = async () => {
    if (!selectedAdjustmentDate || !selectedMizan) {
      toast.warning(t("GLOBAL.choose_date") || "يرجى اختيار التاريخ اولا");
      return;
    }

    setDiffCalculationLoading(true);
    setCalculatedDiffData(null);

    const jwt = cookies.get("jwt");

    try {
      const formattedDate = helpers.formatDateTime(selectedAdjustmentDate);
      const result = await dashboardAPI.getMizanWeightDifferenceAtDate({
        params: {
          ...headerObject,
          mizan_id: selectedMizan.mizan_id,
          selected_date: formattedDate,
        },
        headers: {
          jwt: jwt
        }
      });

      if (result?.status && result?.data?.status) {
        setCalculatedDiffData(result.data.data);
        // Convert kg to tons for editing
        setAdjustmentValue(parseFloat(((result.data.data.difference || 0) / 1000).toFixed(2)));
      } else {
        toast.error(result?.data?.message || t("NETWORK_MESSAGES.unknownError"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("NETWORK_MESSAGES.unknownError"));
    } finally {
      setDiffCalculationLoading(false);
    }
  };

  // Submit adjustment
  const handleSubmitAdjustment = async () => {
    if (!selectedMizan || !selectedAdjustmentDate) return;

    setAdjustmentLoading(true);
    const jwt = cookies.get("jwt");

    try {
      const result = await dashboardAPI.createAdjustmentTransaction({
        params: headerObject,
        headers: {
          jwt: jwt
        },
        data: {
          mizan_id: selectedMizan.mizan_id,
          // Convert tons back to kg for the backend
          liters: adjustmentValue * 1000,
          note: adjustmentNote || `تعديل وزن - ${selectedMizan.mizan_name}`,
          created_at: helpers.formatDateTime(selectedAdjustmentDate)
        }
      });

      if (result?.status && result?.data) {
        toast.success(t("TRANSACTIONS.adjustment_created_success") || `تم انشاء مستند التعديل بنجاح: ${result?.data?.transaction_number}`);
        handleCloseAdjustment();
        setReload(!reload); // Reload data
      } else {
        toast.error(result?.data?.message || t("NETWORK_MESSAGES.unknownError"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("NETWORK_MESSAGES.unknownError"));
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const [columns, setColumns] = useState([
    { field: "index_column", headerName: "ت", minWidth: 50, maxWidth: 60 },
    { field: "mizan_name", headerName: t("TRANSACTIONS.mizan_name"), minWidth: 200, flex: 2 },
    { 
      field: "latest_adjustment_date", 
      headerName: t("TRANSACTIONS.latest_adjustment_date") || "تاريخ اخر تصفير", 
      minWidth: 170, 
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "Cairo-Medium", fontSize: "13px" }}>
          {params.row.latest_adjustment_date ? moment(params.row.latest_adjustment_date).format('YYYY-MM-DD HH:mm') : "---"}
        </Typography>
      )
    },
    { 
      field: "total_internal_weight", 
      headerName: t("TRANSACTIONS.total_internal_weight"), 
      minWidth: 150, 
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "Cairo-Medium" }}>
          {((params.row.total_internal_liters || 0) / 1000).toFixed(2)}
        </Typography>
      )
    },
    { 
      field: "total_external_weight", 
      headerName: t("TRANSACTIONS.total_external_weight"), 
      minWidth: 150, 
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "Cairo-Medium" }}>
          {((params.row.total_external_liters || 0) / 1000).toFixed(2)}
        </Typography>
      )
    },
    { 
      field: "weight_difference", 
      headerName: t("TRANSACTIONS.cumulative_weight") || "متراكم (طن)", 
      minWidth: 150, 
      flex: 1,
      renderCell: (params) => {
        const internal = parseFloat(params.row.total_internal_liters || 0);
        const external = parseFloat(params.row.total_external_liters || 0);
        const adjustment = parseFloat(params.row.total_adjustment_liters || 0);
        const diff = (internal - external + adjustment) / 1000;
        return (
          <Typography sx={{ 
            fontFamily: "Cairo-Medium",
            color: diff > 0 ? '#d32f2f' : diff < 0 ? '#2e7d32' : 'inherit',
            fontWeight: 'bold'
          }}>
            {diff.toFixed(2)}
          </Typography>
        );
      }
    },
    {
      field: "actions",
      headerName: t("GLOBAL.settlement") || "تصفير",
      minWidth: 120,
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={() => handleOpenAdjustment(params.row)}
          sx={{
            fontFamily: "Cairo-Medium",
            fontSize: "12px",
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            }
          }}
        >
          {t("TRANSACTIONS.settle") || "تصفير"}
        </Button>
      )
    },
  ]);

  useEffect(() => {
    loadData();
  }, [pageSize, reload]); // Only reload data on search/reload or page size change

  const loadData = async () => {
    if (
      cookies.get("active_session") &&
      cookies.get("active_session") == "yes"
    ) {
      let object = {
        ...headerObject,
        ...filters,
      };

      setLoading(true);

      try {
        const result = await dashboardAPI.getWeightTotalsPerMizan({
          params: object,
          signal: controller?.signal,
        });
        setLoading(false);

        if (result.status && result?.data?.data) {
          const allData = result?.data?.data;

          // Process Category 1: Transformative (ID 1)
          const dataCat1 = allData.filter(item => item.port_category_id === 1).map((item, index) => ({
            ...item,
            id: item.mizan_id,
            index_column: index + 1,
          }));
          setRowsCat1(dataCat1);
          setRowsTotalCat1(dataCat1.length);
          setPageCountCat1(Math.ceil(dataCat1.length / pageSize));

          // Process Category 2: Press (ID 2)
          const dataCat2 = allData.filter(item => item.port_category_id === 2).map((item, index) => ({
            ...item,
            id: item.mizan_id,
            index_column: index + 1,
          }));
          setRowsCat2(dataCat2);
          setRowsTotalCat2(dataCat2.length);
          setPageCountCat2(Math.ceil(dataCat2.length / pageSize));
        } else {
          setRowsCat1([]);
          setRowsTotalCat1(0);
          setRowsCat2([]);
          setRowsTotalCat2(0);

          if (result?.data?.data?.message) {
            toast.error(result?.data?.data?.message?.text ? result?.data?.data?.message?.text : result?.data?.data?.message);
          } else if (!result?.data?.error) {
            toast.error(t("NETWORK_MESSAGES.unknownError"));
          }
          if (result?.data?.data?.message?.text == "unauthorized to complete") {
            navigate("/redirect");
          }
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    } else {
      navigate("/redirect");
    }
  };

  const clearFilterFunction = () => {
    setFilters({
      creation_date_to: moment().endOf('day').format('YYYY-MM-DD') + " 23:59:59",
      creationDateTo: todayEnd,
      all_time: 0
    });
  };

  const searchFunction = () => {
    setReload(!reload);
    setPageNumberCat1(1);
    setPageNumberCat2(1);
  };

  const getTitleAndSecondaryTitle = () => {
    let title = t('TRANSACTIONS.manage_mizan_weight');

    let secondaryTitle = " من تاريخ اختر تصفير ";
    if (filters?.creation_date_to) {
      secondaryTitle += (t('GLOBAL.to') + ": " + filters?.creation_date_to);
    }
    return {
      title: title,
      secondaryTitle: secondaryTitle
    };
  };

  const commonGridProps = {
    fixHeight: null, // Allow grids to grow with content
    hasExport: false,
    columns: columns,
    pageSize: pageSize,
    setPageSize: setPageSize,
    loading: loading,
    checkScreenSize: screenwidth,
    openFilterColumn: openFilterColumn,
    elementClicked: elementClicked,
    setOpenFilterColumn: setOpenFilterColumn,
    setColumnFilterValue: setOpenFilterColumnValue,
    columFilterValue: openFilterColumnValue,
    clearFunction: clearFilterFunction,
    searchFunction: searchFunction,
    hasCreate: false,
    hasColumnsHidShow: false,
    setColumns: setColumns,
    filterChilds: (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <CustomDateTimePicker
          label={t("GLOBAL.to") || "الى"}
          value={filters?.creationDateTo}
          onChange={handleFilterDateChange}
          onClearClick={() => {
            setFilters({
              ...filters,
              creation_date_to: null,
              creationDateTo: null
            });
          }}
        />
      </Box>
    )
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        justifyContent: "center",
        alignItems: "start",
        overflowX: "hidden",
        "& .MuiChip-label": {
          fontFamily: "Cairo-Medium",
        },
      }}
    >
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, p: 2, overflowY: 'auto', maxHeight: '100vh' }}>
        {/* Grid 1: Transformative (تحويلية) */}
        {rolesRedux?.view_mizan_weight_management_cat1?.value && (
          <Grid
            {...commonGridProps}
            rows={rowsCat1}
            pageNumber={pageNumberCat1}
            setPageNumber={setPageNumberCat1}
            handlePageChange={(newPage) => setPageNumberCat1(newPage + 1)}
            rowsTotal={rowsTotalCat1}
            pageCount={pageCountCat1}
            LeftHeaderComponent={
              <Title
                title={getTitleAndSecondaryTitle()?.title + " - تحويلية"}
                secondaryTitle={getTitleAndSecondaryTitle()?.secondaryTitle}
              />
            }
          />
        )}

        {/* Grid 2: Press (مكبس) */}
        {rolesRedux?.view_mizan_weight_management_cat2?.value && (
          <Grid
            {...commonGridProps}
            rows={rowsCat2}
            pageNumber={pageNumberCat2}
            setPageNumber={setPageNumberCat2}
            handlePageChange={(newPage) => setPageNumberCat2(newPage + 1)}
            rowsTotal={rowsTotalCat2}
            pageCount={pageCountCat2}
            LeftHeaderComponent={
              <Title
                title={getTitleAndSecondaryTitle()?.title + " - مكبس"}
                secondaryTitle={getTitleAndSecondaryTitle()?.secondaryTitle}
              />
            }
          />
        )}
      </Box>

      {/* Adjustment Dialog */}
      <Dialog
        open={adjustmentDialogOpen}
        onClose={handleCloseAdjustment}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 400 }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e2e8f0',
          pb: 2,
        }}>
          <Typography sx={{ fontFamily: 'Cairo-Bold', fontSize: '18px', color: '#1e293b' }}>
            {t("TRANSACTIONS.adjustment_dialog_title") || "تصفير وزن الميزان"}
          </Typography>
          <IconButton onClick={handleCloseAdjustment} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Header Info: Mizan & Date in one row if screen allows, otherwise stack */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: 2, 
              alignItems: 'flex-start',
              bgcolor: 'rgba(226, 232, 240, 0.3)',
              p: 2,
              borderRadius: 2,
            }}>
              {/* Mizan Name */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: 'Cairo-Medium', fontSize: '13px', color: '#64748b', mb: 0.2 }}>
                  {t("TRANSACTIONS.mizan_name") || "اسم الميزان"}
                </Typography>
                <Typography sx={{ fontFamily: 'Cairo-Bold', fontSize: '15px', color: '#1e293b' }}>
                  {selectedMizan?.mizan_name}
                </Typography>
              </Box>
              <Box sx={{flex: 2, display: 'flex', gap: 2, mb: 1.5, alignItems: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <CustomDateTimePicker
                    label={ "اختر"}
                    value={selectedAdjustmentDate}
                    onChange={handleDateChange}
                    onClearClick={() => {
                      setSelectedAdjustmentDate(null);
                      setCalculatedDiffData(null);
                    }}
                  />
                </Box>

                {/* Calculate Button */}
                <Box>
                  <Button
                    variant="contained"
                    onClick={calculateWeightDifference}
                    disabled={diffCalculationLoading || !selectedAdjustmentDate}
                    startIcon={diffCalculationLoading ? <CircularProgress size={16} /> : <CalendarMonthIcon />}
                    sx={{
                      fontFamily: 'Cairo-Bold',
                      height: 54,
                      px: 3,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)',
                      },
                      '&.Mui-disabled': {
                        background: '#e2e8f0',
                      }
                    }}
                  >
                    {t("GLOBAL.calculate") || "احتساب"}
                  </Button>
                </Box>
            </Box>
          </Box>

          {!calculatedDiffData && !diffCalculationLoading && selectedAdjustmentDate && (
              <Box sx={{ 
                p: 2, 
                bgcolor: 'rgba(255, 152, 0, 0.05)', 
                border: '1px dashed #ff9800', 
                borderRadius: 2,
                textAlign: 'center'
              }}>
                <Typography sx={{ fontFamily: 'Cairo-Medium', fontSize: '13px', color: '#ed6c02' }}>
                  {t("GLOBAL.click_calculate_hint") || "الرجاء الضغط على احتساب لجلب تفاصيل الاوزان"}
                </Typography>
              </Box>
            )}

            {diffCalculationLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={30} />
              </Box>
            )}

            {calculatedDiffData && !diffCalculationLoading && (
              <Fade in={true}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: 'rgba(99, 102, 241, 0.04)',
                    border: '1px dashed rgba(99, 102, 241, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', bgcolor: '#fff', p: 1, borderRadius: 1 }}>
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography sx={{ color: '#64748b', fontSize: '12px', mb: 0.5 }}>
                          {t("TRANSACTIONS.total_internal_weight_tone") || "Total Internal (tone)"}
                        </Typography>
                        <Typography sx={{ fontFamily: 'Cairo-Bold', fontSize: '16px', color: '#10b981' }}>
                          {((calculatedDiffData.total_internal_liters || 0) / 1000).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ width: '1px', bgcolor: '#e2e8f0', alignSelf: 'stretch' }} />
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography sx={{ color: '#64748b', fontSize: '12px', mb: 0.5 }}>
                          {t("TRANSACTIONS.total_external_weight_tone") || "Total External (tone)"}
                        </Typography>
                        <Typography sx={{ fontFamily: 'Cairo-Bold', fontSize: '16px', color: '#ef4444' }}>
                          {((calculatedDiffData.total_external_liters || 0) / 1000).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                      <Typography sx={{ color: '#6366f1', fontSize: '11px', bgcolor: 'rgba(99, 102, 241, 0.08)', px: 1.5, py: 0.5, borderRadius: 1 }}>
                        {t("TRANSACTIONS.calculation_since") || "Calculation Since"}: {calculatedDiffData.last_adjustment_date}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Weight Difference & Note Row */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label={t("TRANSACTIONS.adjustment_weight_tone") || "قيمة التعديل (طن)"}
                      type="number"
                      size="small"
                      sx={{ flex: 1 }}
                      value={adjustmentValue}
                      onChange={(e) => setAdjustmentValue(parseFloat(e.target.value) || 0)}
                      InputProps={{ sx: { fontFamily: 'Cairo-Medium' } }}
                      InputLabelProps={{ sx: { fontFamily: 'Cairo-Medium' } }}
                    />
                    <TextField
                      label={t("GLOBAL.note") || "ملاحظة"}
                      size="small"
                      sx={{ flex: 2 }}
                      value={adjustmentNote}
                      onChange={(e) => setAdjustmentNote(e.target.value)}
                      InputProps={{ sx: { fontFamily: 'Cairo-Medium' } }}
                      InputLabelProps={{ sx: { fontFamily: 'Cairo-Medium' } }}
                    />
                  </Box>
                </Box>
              </Fade>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0.5 }}>
          <Button
            onClick={handleCloseAdjustment}
            sx={{ fontFamily: 'Cairo-Medium', color: '#64748b' }}
          >
            {t("GLOBAL.cancel") || "الغاء"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitAdjustment}
            disabled={adjustmentLoading || !selectedAdjustmentDate || diffCalculationLoading}
            sx={{
              fontFamily: 'Cairo-Medium',
              px: 4,
              py: 0.8,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              },
              '&.Mui-disabled': {
                background: '#e2e8f0',
                color: '#94a3b8'
              }
            }}
          >
            {adjustmentLoading ? <CircularProgress size={24} color="inherit" /> : (t("GLOBAL.save") || "حفظ")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
